// THL (UK) Holdings Limited — the UK & Ireland arm of the Dubai-based Taurani
// Holdings steel conglomerate. Figures keyed to its audited group accounts
// filed at Companies House (company 04871307), which the Finance Analyst agent
// reads (backend: "taurani docs/"):
//
//   FY21        — Group accounts to 30 June 2022 (comparative)
//   FY22        — Group accounts to 30 June 2023 (restated comparative)
//   FY23        — Group accounts to 30 June 2024 (restated comparative)
//   FY24, FY25  — Group accounts to 30 June 2025 (FY24 restated comparative)
//
// Financial years end 30 June. All amounts are GBP (whole pounds) exactly as
// printed; the latest published figure is used for every year. Nothing here
// is derived — the dashboard owns every ratio and roll-up.
//
// This is NOT the whole Taurani group: the UAE, Oman, Australia, India and US
// businesses do not file public accounts.

export const COMPANY = {
  name: 'THL (UK) Holdings',
  legalName: 'THL (UK) Holdings Limited',
  registration: 'Companies House 04871307',
  parent: 'Taurani Holdings Ltd (Bahamas), headquartered in Dubai',
  basis: 'Consolidated · FRS 102 · audited',
  units: 'GBP',
};

export const YEARS = ['FY21', 'FY22', 'FY23', 'FY24', 'FY25'];

export const YEAR_META = {
  FY21: { endedOn: '30 Jun 2021', source: 'Group accounts to 30 June 2022 (comparative)' },
  FY22: { endedOn: '30 Jun 2022', source: 'Group accounts to 30 June 2023 (restated comparative)' },
  FY23: { endedOn: '30 Jun 2023', source: 'Group accounts to 30 June 2024 (restated comparative)' },
  FY24: { endedOn: '30 Jun 2024', source: 'Group accounts to 30 June 2025 (restated comparative)' },
  FY25: { endedOn: '30 Jun 2025', source: 'Group accounts to 30 June 2025' },
};

// ---------- income statement ----------
// Series ordered [FY21, FY22, FY23, FY24, FY25]. Costs are negative.
export const INCOME = {
  turnover: [133026351, 188609770, 176454483, 147137133, 136645015],
  costOfSales: [-100636114, -140312011, -148153310, -123056498, -112047388],
  grossProfit: [32390237, 48297759, 28301173, 24080635, 24597627],
  distributionCosts: [-2450703, -2871345, -2503128, -2389233, -2844648],
  adminExpenses: [-19617013, -24224608, -23497943, -24270056, -24147775],
  otherOperatingIncome: [445242, 277063, 293879, 286651, 285985],
  operatingProfit: [10767763, 21478869, 2593981, -2292003, -2108811],
  interestReceivable: [62, 0, 1516, 0, 35888],
  interestPayable: [-1740423, -2035490, -2936887, -3573271, -2858816],
  profitBeforeTax: [9027402, 19443379, -341390, -5865274, -4931739],
  tax: [-1698735, -3827488, -257880, 1216115, 289526],
  profitAfterTax: [7328667, 15615891, -599270, -4649159, -4642213],
  toOwners: [5204832, 10984153, -592599, -4447052, -4642213],
  toNci: [2123835, 4631738, -6671, -202107, 0],
};

// Depreciation and amortisation, from the cash-flow reconciliation note.
export const DEPRECIATION = [1211889, 1874439, 2821384, 3021084, 3041751];

// ---------- balance sheet (30 June) ----------
export const BALANCE_SHEET = {
  intangibles: [724625, 5318982, 4911227, 4321904, 3732598],
  tangibles: [23841666, 23394453, 23806288, 24413109, 23707218],
  investmentProperty: [1422709, 1422709, 1422709, 1422709, 1422709],
  fixedAssets: [25989000, 30136144, 30140224, 30157722, 28862525],
  stocks: [21171764, 38186706, 36422225, 29391506, 31463048],
  debtors: [42973875, 47584873, 41464148, 41031342, 34016890],
  currentInvestments: [160341, 0, 0, 0, 0],
  cash: [7214676, 1136348, 1721386, 1972316, 761269],
  currentAssets: [71520656, 86907927, 79607759, 72395164, 66241207],
  creditorsWithinYear: [-62110533, -66997732, -68038152, -65613823, -57429509],
  netCurrentAssets: [9410123, 19910195, 11569607, 6781341, 8811698],
  creditorsAfterYear: [-24590658, -30169391, -21665668, -22686205, -28515598],
  provisions: [-503182, -947806, -1551797, -409651, -120125],
  netAssets: [10305283, 18929142, 18492366, 13843207, 9038500],
  shareCapital: [18000, 18000, 18000, 18000, 18000],
  sharePremium: [272000, 272000, 434494, 434494, 272000],
  retainedEarnings: [7654989, 18639142, 18046543, 13599491, 8748500],
  shareholdersFunds: [7944989, 18929142, 18499037, 14051985, 9038500],
  nci: [2360294, 0, -6671, -208778, 0],
};

// ---------- cash flow ----------
// FY24 is the restated comparative from the FY25 accounts, which moved £1.8m
// of related-party loans from operating to financing.
export const CASH_FLOW = {
  cashFromOperations: [18875832, 10437707, 10960834, 3712688, -2186559],
  netOperating: [17050316, 5970377, 5770385, -44491, -4728549],
  netInvesting: [-2983903, -5301340, -2705518, -3039544, -1713903],
  netFinancing: [743338, -6747365, -2479829, 3334965, 5210888],
  capexTangible: [-2558302, -5049038, -2730175, -2879310, -1584640],
  capexIntangible: [-410442, -4859124, -402525, -287351, -201792],
  netChange: [14809751, -6078328, 585038, 250930, -1231564],
};

// ---------- FY24-FY25 note disclosures ----------
// Columns [FY24, FY25].
export const NOTE_YEARS = ['FY24', 'FY25'];

export const FUNDING = {
  relatedPartyLoans: [21196253, 28044418], // Taurani Holdings (Bahamas); no fixed repayment date
  relatedPartyTradeCreditors: [13651959, 16572302],
  invoiceDiscounting: [26966986, 23542828],
  bankLoansOverdrafts: [2406972, 1658580],
  hirePurchase: [1118250, 429695], // within + after one year
  tradeCreditors: [15983956, 10699736],
};

export const WORKING_CAPITAL = {
  tradeDebtors: [38476038, 32226067],
  rawMaterials: [16660040, 19946164],
  finishedGoods: [12731466, 11516884],
};

// Purchases from Taurani sister companies (note 27), FY22-FY25. The FY21
// figure is not carried in the files used here.
export const RELATED_PARTY_YEARS = ['FY22', 'FY23', 'FY24', 'FY25'];
export const RELATED_PARTY = {
  purchasesFromGroup: [86387746, 81651443, 52829711, 49313298],
  salesToGroup: [516050, 1117087, 1302573, 32620],
};

// Note 24: HMRC retrospective safeguard-duty and import-VAT assessment on
// Parkersteel (post-Brexit CHIEFS issue), being defended in a group action.
export const HMRC_CLAIM = 1200000;

// Subsidiary results (note 11), profit/(loss) for the year.
export const SUBSIDIARIES = [
  { name: 'Parkersteel Ltd', business: 'Steel manufacturer, distributor and stockholder (Canterbury)', pat: [-4042133, -4275446], equity: [8479334, 4203888] },
  { name: 'Brisko Scaffolding Ltd', business: 'Scaffolding and other steel materials (Ipswich)', pat: [149231, -454298], equity: [7111082, 6656684] },
  { name: 'Total Safety Solutions International Ltd', business: 'Safety equipment (Ipswich)', pat: [26182, 32936], equity: [-152287, -119351] },
  { name: 'Midland Steel Sections Ltd', business: 'Dormant', pat: [0, -192], equity: [925059, 924867] },
];

export const PEOPLE = {
  employees: [374, 345],
  staffCosts: [14512026, 14079945],
};

export const GEOGRAPHY = [
  { region: 'United Kingdom', values: [140830615, 131196530] },
  { region: 'Europe', values: [6019278, 5430074] },
  { region: 'Rest of world', values: [287240, 18411] },
];

// Sister companies the UK group owes money to at 30 June (note 27).
export const OWED_TO_GROUP_FY25 = [
  { name: 'THL Global Holdings Ltd', value: 28627892 },
  { name: 'KHK Scaffolding & Formwork LLC', value: 4939794 },
  { name: 'DSS Steel FZE', value: 4258908 },
  { name: 'Universal Tube & Plastic Industries FZE', value: 3780810 },
  { name: 'THL Tube & Pipe Industries LLC', value: 2827572 },
  { name: 'TSS Trading LLC', value: 159668 },
  { name: 'Ducast Factory LLC', value: 22076 },
];

export const EMISSIONS_FY25 = { scope1: 4388, scope2: 563, scope3: 24, total: 4975 };
